import React from 'react';
import { motion } from 'framer-motion';
import { ArrowsRightLeftIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { TeaserDemoProps } from '../../config/teaserContent';
import { createOptimizedTeaserDemo, useTeaserDemoState } from './baseTeaserDemo';
import { mockSwapData } from '../../data/teaserMockData';

interface AIPortfolioStrategistDemoProps extends TeaserDemoProps {
  recommendations?: typeof mockSwapData.strategies;
  symbol?: string;
}

const AIPortfolioStrategistDemoComponent: React.FC<AIPortfolioStrategistDemoProps> = ({ 
  className, 
  aspectRatio = 'video',
  recommendations = mockSwapData.strategies.slice(0, 2), // Show first 2 recommendations
  symbol = mockSwapData.symbol
}) => {
  const { containerClass } = useTeaserDemoState(aspectRatio);

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={`relative p-4 h-full flex flex-col justify-between ${containerClass} ${className}`}
    >
      {/* Animated Header */}
      <div className="flex items-center gap-3 mb-4">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg"
        >
          <ArrowsRightLeftIcon className="w-4 h-4 text-white" />
        </motion.div>
        <div>
          <h4 className="text-white font-semibold text-sm">AI Recommendation Engine</h4>
          <p className="text-gray-300 text-xs">{recommendations.length} strategies analyzed</p>
        </div>
      </div>

      {/* Animated Action Cards */}
      <div className="space-y-2">
        {recommendations.map((rec, index) => (
          <motion.div
            key={rec.name}
            animate={{ 
              backgroundColor: rec.action === 'promote' 
                ? ['rgba(34, 197, 94, 0.2)', 'rgba(34, 197, 94, 0.3)', 'rgba(34, 197, 94, 0.2)']
                : ['rgba(239, 68, 68, 0.2)', 'rgba(239, 68, 68, 0.3)', 'rgba(239, 68, 68, 0.2)'],
              scale: [1, 1.02, 1]
            }}
            transition={{ duration: 2.5, repeat: Infinity, delay: index * 0.5 }}
            className={`flex items-center gap-2 p-3 rounded-lg border ${
              rec.action === 'promote' 
                ? 'border-green-500/30' 
                : 'border-red-500/30'
            } backdrop-blur-sm`}
          >
            {rec.action === 'promote' ? (
              <ArrowUpIcon className="w-4 h-4 text-green-400" />
            ) : (
              <ArrowDownIcon className="w-4 h-4 text-red-400" />
            )}
            <div className="flex-1">
              <span className={`${
                rec.action === 'promote' ? 'text-green-300' : 'text-red-300'
              } text-sm font-medium`}>
                {rec.action === 'promote' ? 'PROMOTE' : 'DEMOTE'} {rec.name}
              </span>
              <div className="text-xs text-gray-400 flex items-center gap-2">
                <span>{rec.fromAccount} → {rec.toAccount}</span>
                <span className={`font-semibold ${
                  rec.action === 'promote' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {rec.performance}
                </span>
              </div>
            </div>
          </motion.div>
        ))}

        {/* Connection Animation */}
        <div className="flex items-center justify-center py-3">
          <motion.div
            animate={{ width: ['20%', '80%', '20%'] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="h-0.5 bg-gradient-to-r from-red-400 via-purple-400 to-green-400 rounded-full shadow-sm"
          />
        </div>

        {/* Symbol Display */}
        <div className="text-center">
          <motion.p
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-blue-300 text-xs font-medium"
          >
            📊 Analyzing {symbol} strategies
          </motion.p>
        </div>
      </div>
    </motion.div>
  );
};

export const AIPortfolioStrategistDemo = createOptimizedTeaserDemo(
  AIPortfolioStrategistDemoComponent,
  'AIPortfolioStrategistDemo'
);